import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { AnalyticsPeriod, AnalyticsState } from '../types';
import { analyticsService } from '../api/analyticsService';
import { switchWorkspace } from '@/features/workspace/workspaceSlice';
import { AppError as HttpAppError } from '@/http/errors';
import { mapErrorMessage } from '@/lib/mapApiError';

/**
 * Interface for the overview data fetching payload.
 * Supports both named period and explicit ISO dates.
 */
export interface FetchOverviewDataPayload {
  workspaceId: number;
  from?: string;
  to?: string;
}

/**
 * Thunk to fetch global workspace analytics overview.
 * Uses explicit from/to dates provided by the component.
 */
export const fetchOverviewData = createAsyncThunk(
  'analytics/fetchOverviewData',
  async (fetchPayload: FetchOverviewDataPayload) => {
    try {
      const { workspaceId, from, to } = fetchPayload;

      const [
        statsData,
        registrationsGraphData,
        offerClicksGraphData,
        geographicData,
        technicalData,
      ] = await Promise.all([
        analyticsService.getSummary(workspaceId, from, to),
        analyticsService.getRegistrationsGraph(workspaceId, from, to),
        analyticsService.getOfferClicksGraph(workspaceId, from, to),
        analyticsService.getGeographicDistribution(workspaceId, from, to),
        analyticsService.getTechnicalDistribution(workspaceId, from, to),
      ]);

      return {
        stats: statsData,
        registrationsGraph: registrationsGraphData,
        offerClicksGraph: offerClicksGraphData,
        geographic: geographicData,
        technical: technicalData,
      };
    } catch (requestError) {
      console.warn('Failed to fetch analytics overview data:', requestError);
      throw requestError;
    }
  },
);

export const fetchPastWebinarsData = createAsyncThunk(
  'analytics/fetchPastWebinarsData',
  async ({ workspaceId, page, size }: { workspaceId: number; page: number; size: number }) => {
    return await analyticsService.getPastWebinars(workspaceId, page, size);
  },
);

export const fetchSessionParticipants = createAsyncThunk(
  'analytics/fetchParticipants',
  async ({
    workspaceId,
    sessionId,
    page,
    size,
  }: {
    workspaceId: number;
    sessionId: number;
    page: number;
    size: number;
  }) => {
    return await analyticsService.getSessionParticipants(workspaceId, sessionId, page, size);
  },
);

export const fetchSessionDetailsData = createAsyncThunk(
  'analytics/fetchSessionDetailsData',
  async ({ workspaceId, sessionId }: { workspaceId: number; sessionId: number }) => {
    try {
      return await analyticsService.getSessionDetails(workspaceId, sessionId);
    } catch (err) {
      if (err instanceof HttpAppError) {
        throw new Error(mapErrorMessage(err.message, err.code, err.status));
      }
      const e = err as { code?: string; message?: string; status?: number };
      throw new Error(mapErrorMessage(e.message, e.code, e.status));
    }
  },
);

export const fetchParticipantFullDetailsData = createAsyncThunk(
  'analytics/fetchParticipantFullDetailsData',
  async ({
    workspaceId,
    sessionId,
    attendeeId,
  }: {
    workspaceId: number;
    sessionId: number;
    attendeeId: number;
  }) => {
    return await analyticsService.getParticipantFullDetails(workspaceId, sessionId, attendeeId);
  },
);

export const fetchComparisonData = createAsyncThunk(
  'analytics/fetchComparisonData',
  async ({
    workspaceId,
    sessionIdA,
    sessionIdB,
  }: {
    workspaceId: number;
    sessionIdA: number;
    sessionIdB: number;
  }) => {
    return await analyticsService.getFullComparison(workspaceId, sessionIdA, sessionIdB);
  },
);

export const fetchReplayDownload = createAsyncThunk(
  'analytics/fetchReplayDownload',
  async (
    {
      workspaceId,
      sessionId,
      type,
    }: { workspaceId: number; sessionId: number; type: 'desktop' | 'mobile' },
    { rejectWithValue },
  ) => {
    try {
      const url = analyticsService.getReplayDownloadUrl(workspaceId, sessionId, type);
      window.open(url, '_blank', 'noopener,noreferrer');
      return { type };
    } catch (err) {
      return rejectWithValue((err as Error).message || 'FAILED_TO_DOWNLOAD_REPLAY');
    }
  },
);

function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function prospectsExportFilename(sessionId: number, blob: Blob): string {
  if (blob.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
    return `prospects-session-${sessionId}.xlsx`;
  }
  if (blob.type === 'text/csv' || blob.type === 'application/csv') {
    return `prospects-session-${sessionId}.csv`;
  }
  return `prospects-session-${sessionId}`;
}

function chatExportFilename(sessionId: number, blob: Blob): string {
  if (blob.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
    return `chat-session-${sessionId}.xlsx`;
  }
  if (blob.type === 'text/csv' || blob.type === 'application/csv') {
    return `chat-session-${sessionId}.csv`;
  }
  return `chat-session-${sessionId}`;
}

export const exportSessionProspects = createAsyncThunk<
  void,
  { workspaceId: number; sessionId: number },
  { rejectValue: string }
>('analytics/exportSessionProspects', async ({ workspaceId, sessionId }, { rejectWithValue }) => {
  try {
    const blob = await analyticsService.exportSessionProspects(workspaceId, sessionId);
    triggerBlobDownload(blob, prospectsExportFilename(sessionId, blob));
  } catch (err) {
    return rejectWithValue((err as Error).message || 'FAILED_TO_EXPORT_PROSPECTS');
  }
});

export const exportSessionChatComplete = createAsyncThunk<
  void,
  { workspaceId: number; sessionId: number },
  { rejectValue: string }
>(
  'analytics/exportSessionChatComplete',
  async ({ workspaceId, sessionId }, { rejectWithValue }) => {
    try {
      const blob = await analyticsService.exportSessionChatComplete(workspaceId, sessionId);
      triggerBlobDownload(blob, chatExportFilename(sessionId, blob));
    } catch (err) {
      return rejectWithValue((err as Error).message || 'FAILED_TO_EXPORT_CHAT');
    }
  },
);

const initialState: AnalyticsState = {
  overview: {
    stats: null,
    registrationsGraph: null,
    offerClicksGraph: null,
    geographic: null,
    technical: null,
    period: 'TOTAL',
    loading: false,
    error: null,
  },
  engagement: {
    list: [],
    loading: false,
    error: null,
  },
  pastWebinars: {
    list: [],
    loading: false,
    error: null,
    totalPages: 0,
    totalElements: 0,
  },
  currentSession: {
    data: null,
    sessionId: null,
    loading: false,
    error: null,
  },
  participantsData: {
    items: [],
    totalElements: 0,
    totalPages: 0,
    currentPage: 0,
  },
  selectedParticipant: {
    id: null,
    details: null,
    loading: false,
    error: null,
    isOpen: false,
  },
  comparison: {
    data: null,
    loading: false,
    error: null,
    sessionAId: null,
    sessionBId: null,
  },
  loading: false,
  participantsLoading: false,
  replayDownload: {
    loading: false,
    error: null,
  },
  prospectsExport: {
    loading: false,
    error: null,
  },
  chatExport: {
    loading: false,
    error: null,
  },
};

const analyticsSlice = createSlice({
  name: 'analytics',
  initialState,
  reducers: {
    setOverviewPeriod(state, action: PayloadAction<AnalyticsPeriod>) {
      state.overview.period = action.payload;
    },
    setCurrentParticipantsPage(state, action: PayloadAction<number>) {
      state.participantsData.currentPage = action.payload;
    },
    clearCurrentSession(state) {
      state.currentSession.data = null;
      state.currentSession.sessionId = null;
      state.currentSession.error = null;
    },
    openParticipantSidebar(state, action: PayloadAction<number>) {
      state.selectedParticipant.id = action.payload;
      state.selectedParticipant.isOpen = true;
      state.selectedParticipant.details = null;
    },
    closeParticipantSidebar(state) {
      state.selectedParticipant.isOpen = false;
      state.selectedParticipant.id = null;
    },
    reset: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOverviewData.pending, (state) => {
        state.overview.loading = true;
        state.overview.error = null;
      })
      .addCase(fetchOverviewData.fulfilled, (state, action) => {
        state.overview.loading = false;
        state.overview.stats = action.payload.stats;
        state.overview.registrationsGraph = action.payload.registrationsGraph;
        state.overview.offerClicksGraph = action.payload.offerClicksGraph;
        state.overview.geographic = action.payload.geographic;
        state.overview.technical = action.payload.technical;
      })
      .addCase(fetchOverviewData.rejected, (state, action) => {
        state.overview.loading = false;
        state.overview.error = action.error.message || 'FAILED_TO_FETCH_ANALYTICS';
      })
      .addCase(fetchPastWebinarsData.pending, (state) => {
        state.pastWebinars.loading = true;
        state.pastWebinars.error = null;
      })
      .addCase(fetchPastWebinarsData.fulfilled, (state, action) => {
        state.pastWebinars.loading = false;
        state.pastWebinars.list = action.payload.content;
        state.pastWebinars.totalPages = action.payload.totalPages;
        state.pastWebinars.totalElements = action.payload.totalElements;
      })
      .addCase(fetchPastWebinarsData.rejected, (state, action) => {
        state.pastWebinars.loading = false;
        state.pastWebinars.error = action.error.message || 'FAILED_TO_FETCH_PAST_WEBINARS';
      })
      .addCase(fetchSessionDetailsData.pending, (state, action) => {
        state.currentSession.loading = true;
        state.currentSession.sessionId = action.meta.arg.sessionId;
        state.currentSession.error = null;
      })
      .addCase(fetchSessionDetailsData.fulfilled, (state, action) => {
        state.currentSession.loading = false;
        state.currentSession.sessionId = action.meta.arg.sessionId;
        state.currentSession.data = action.payload;
      })
      .addCase(fetchSessionDetailsData.rejected, (state, action) => {
        state.currentSession.loading = false;
        state.currentSession.error = action.error.message || 'FAILED_TO_FETCH_SESSION_DETAILS';
      })
      .addCase(fetchParticipantFullDetailsData.pending, (state) => {
        state.selectedParticipant.loading = true;
        state.selectedParticipant.error = null;
      })
      .addCase(fetchParticipantFullDetailsData.fulfilled, (state, action) => {
        state.selectedParticipant.loading = false;
        state.selectedParticipant.details = action.payload;
      })
      .addCase(fetchParticipantFullDetailsData.rejected, (state, action) => {
        state.selectedParticipant.loading = false;
        state.selectedParticipant.error =
          action.error.message || 'FAILED_TO_FETCH_PARTICIPANT_DETAILS';
      })
      .addCase(fetchComparisonData.pending, (state) => {
        state.comparison.loading = true;
        state.comparison.error = null;
      })
      .addCase(fetchComparisonData.fulfilled, (state, action) => {
        state.comparison.loading = false;
        state.comparison.data = action.payload;
      })
      .addCase(fetchComparisonData.rejected, (state, action) => {
        state.comparison.loading = false;
        state.comparison.error = action.error.message || 'FAILED_TO_FETCH_COMPARISON';
      })
      .addCase(fetchSessionParticipants.pending, (state) => {
        state.participantsLoading = true;
      })
      .addCase(fetchSessionParticipants.fulfilled, (state, action) => {
        state.participantsLoading = false;
        state.participantsData.items = action.payload.content;
        state.participantsData.totalElements = action.payload.totalElements;
        state.participantsData.totalPages = action.payload.totalPages;
      })
      .addCase(fetchSessionParticipants.rejected, (state) => {
        state.participantsLoading = false;
      })
      .addCase(fetchReplayDownload.pending, (state) => {
        state.replayDownload.loading = true;
        state.replayDownload.error = null;
      })
      .addCase(fetchReplayDownload.fulfilled, (state) => {
        state.replayDownload.loading = false;
        state.replayDownload.error = null;
      })
      .addCase(fetchReplayDownload.rejected, (state, action) => {
        state.replayDownload.loading = false;
        state.replayDownload.error =
          (action.payload as string) || action.error.message || 'FAILED_TO_DOWNLOAD_REPLAY';
      })
      .addCase(exportSessionProspects.pending, (state) => {
        state.prospectsExport.loading = true;
        state.prospectsExport.error = null;
      })
      .addCase(exportSessionProspects.fulfilled, (state) => {
        state.prospectsExport.loading = false;
        state.prospectsExport.error = null;
      })
      .addCase(exportSessionProspects.rejected, (state, action) => {
        state.prospectsExport.loading = false;
        state.prospectsExport.error =
          (action.payload as string) || action.error.message || 'FAILED_TO_EXPORT_PROSPECTS';
      })
      .addCase(exportSessionChatComplete.pending, (state) => {
        state.chatExport.loading = true;
        state.chatExport.error = null;
      })
      .addCase(exportSessionChatComplete.fulfilled, (state) => {
        state.chatExport.loading = false;
        state.chatExport.error = null;
      })
      .addCase(exportSessionChatComplete.rejected, (state, action) => {
        state.chatExport.loading = false;
        state.chatExport.error =
          (action.payload as string) || action.error.message || 'FAILED_TO_EXPORT_CHAT';
      })
      .addCase(switchWorkspace, () => initialState);
  },
});

export const {
  setOverviewPeriod,
  setCurrentParticipantsPage,
  clearCurrentSession,
  openParticipantSidebar,
  closeParticipantSidebar,
  reset: resetAnalytics,
} = analyticsSlice.actions;
export default analyticsSlice.reducer;
